"use client";
import { useCallback, useEffect, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { useCaptureSession } from "./useCaptureSession";

export type CaptureState =
  | { phase: "none" }
  | { phase: "initializing" }
  | {
      phase: "ready";
      stripePromise: Promise<Stripe | null>;
      clientSecret: string;
    }
  | {
      phase: "error";
      message: string;
      retryType: "backend" | "stripe";
      stripePromise?: Promise<Stripe | null>;
      clientSecret?: string;
    }
  | { phase: "success" };

interface Props {
  patientId: string;
  enabled: boolean;
}

export function useCapturePhase({ patientId, enabled }: Props) {
  const [captureState, setCaptureState] = useState<CaptureState>({
    phase: "none",
  });
  const { mutateAsync } = useCaptureSession();

  const handleCapture = useCallback(async () => {
    setCaptureState({ phase: "initializing" });
    try {
      const session = await mutateAsync(patientId);
      if (!session.clientSecret || !session.publishableKey) {
        setCaptureState({
          phase: "error",
          message: "Could not initialize payment. Please try again.",
          retryType: "backend",
        });
        return;
      }
      const stripePromise = loadStripe(session.publishableKey, {
        stripeAccount: session.connectedAccountId ?? undefined,
      });
      setCaptureState({
        phase: "ready",
        stripePromise,
        clientSecret: session.clientSecret,
      });
    } catch {
      setCaptureState({
        phase: "error",
        message: "Could not reach the server. Please try again.",
        retryType: "backend",
      });
    }
  }, [mutateAsync, patientId]);

  useEffect(() => {
    if (enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      handleCapture();
    }
  }, [handleCapture, enabled]);

  const markSuccess = useCallback(
    () => setCaptureState({ phase: "success" }),
    [],
  );

  return {
    captureState,
    markSuccess,
    handleCapture,
  };
}
